import { parseTierBadgeId } from '~/boggle/config/badges/familyBadgeId'
import type { BoggleBadgeId } from '~/boggle/config/badges/types'
import { getLowerTierBadgeIds } from '~/boggle/lib/badges/collapseEarnedBadges'
import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type {
  BoggleBadgeProgressProjection,
  BoggleBadgeProgressRecord,
  BoggleCompletedRun,
} from './types'

const RATING_RANK = new Map<BoggleRunSummaryRatingId, number>(
  BOGGLE_RUN_SUMMARY_RATINGS.map((rating, index) => [rating.id, index]),
)

function getHigherRating(
  current: BoggleRunSummaryRatingId | null,
  candidate: BoggleRunSummaryRatingId,
) {
  if (current === null) {
    return candidate
  }
  return (RATING_RANK.get(candidate) ?? -1) > (RATING_RANK.get(current) ?? -1)
    ? candidate
    : current
}

function compareIsoThenId(a: BoggleCompletedRun, b: BoggleCompletedRun) {
  const completedAtOrder = a.completedAt.localeCompare(b.completedAt)
  return completedAtOrder !== 0 ? completedAtOrder : a.id.localeCompare(b.id)
}

export function createEmptyBoggleBadgeProgressProjection(): BoggleBadgeProgressProjection {
  return {
    perBadge: {},
    profileRunsByRating: {},
  }
}

export function buildBoggleBadgeProgressProjection(
  completedRuns: readonly BoggleCompletedRun[],
): BoggleBadgeProgressProjection {
  const projection = createEmptyBoggleBadgeProgressProjection()

  for (const run of [...completedRuns].sort(compareIsoThenId)) {
    projection.profileRunsByRating[run.ratingId] =
      (projection.profileRunsByRating[run.ratingId] ?? 0) + 1

    // Schema-v1 durable facts have no badge-award history. Do not infer awards
    // by rerunning today's mutable definitions; absence means "not recorded."
    for (const earned of run.earnedBadges ?? []) {
      const badgeIdsToUnlock = badgeIdsIncludingLowerTiers(earned.badgeId)
      for (const badgeId of badgeIdsToUnlock) {
        const previous: BoggleBadgeProgressRecord = projection.perBadge[badgeId] ?? {
          countsByRating: {},
          highestRating: null,
        }

        projection.perBadge[badgeId] = {
          countsByRating: {
            ...previous.countsByRating,
            [run.ratingId]: (previous.countsByRating[run.ratingId] ?? 0) + 1,
          },
          highestRating: getHigherRating(previous.highestRating, run.ratingId),
        }
      }
    }
  }

  return projection
}

export function badgeIdsIncludingLowerTiers(badgeId: BoggleBadgeId): BoggleBadgeId[] {
  const parsed = parseTierBadgeId(badgeId)
  if (!parsed) {
    return [badgeId]
  }
  return [
    ...getLowerTierBadgeIds(parsed.familyId, parsed.tierIndex),
    badgeId,
  ] as BoggleBadgeId[]
}
